import React, { useState } from 'react';
import { FiFileText, FiPrinter, FiMail, FiX, FiDownload } from 'react-icons/fi';
import api from '../../services/api';

/**
 * Document toolbar with PDF download, Print, and Email buttons.
 * Renders at the top of document preview pages.
 *
 * Props:
 *  - title: document title
 *  - onClose: callback for close button
 *  - pdfUrl: server API path to download real PDF (e.g. "/pdf/charge/5/invoice")
 *  - fileName: suggested filename for download
 *  - contentRef: (legacy) ref to HTML element for html2pdf fallback
 */
const DocumentToolbar = ({ title, onClose, pdfUrl, contentRef, fileName }) => {
  const [emailTo, setEmailTo] = useState('');
  const [showEmailModal, setShowEmailModal] = useState(false);
  const [downloading, setDownloading] = useState(false);

  const handlePrint = () => {
    if (pdfUrl) {
      // Open the PDF in a new tab for printing
      handlePDFDownload(true);
    } else {
      window.print();
    }
  };

  const handlePDFDownload = async (openInTab = false) => {
    if (!pdfUrl) {
      // Legacy fallback: use browser print
      window.print();
      return;
    }

    setDownloading(true);
    try {
      const saveParam = openInTab ? '' : (pdfUrl.includes('?') ? '&save=1' : '?save=1');
      const response = await api.get(pdfUrl + saveParam, { responseType: 'blob' });
      const blob = new Blob([response.data], { type: 'application/pdf' });
      const url = window.URL.createObjectURL(blob);

      if (openInTab) {
        // Open in new tab for printing
        window.open(url, '_blank');
      } else {
        // Download
        const a = document.createElement('a');
        a.href = url;
        a.download = fileName || 'document.pdf';
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
      }
      setTimeout(() => window.URL.revokeObjectURL(url), 10000);
    } catch (err) {
      console.error('PDF download failed:', err);
      alert('Failed to generate PDF. Please try again.');
    } finally {
      setDownloading(false);
    }
  };

  const sendEmail = () => {
    const subject = encodeURIComponent(title || 'Document');
    const body = encodeURIComponent(`Please find the attached ${title || 'document'}.\n\nBest regards,\nHW Home Goods Inc.`);
    window.open(`mailto:${emailTo}?subject=${subject}&body=${body}`, '_blank');
    setShowEmailModal(false);
    setEmailTo('');
  };

  return (
    <>
      <div className="doc-toolbar no-print" style={{
        position: 'sticky', top: 0, zIndex: 100,
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        gap: '16px', padding: '10px 20px',
        background: '#1e293b', color: 'white',
        boxShadow: '0 2px 8px rgba(0,0,0,0.2)',
      }}>
        <span style={{ fontSize: '14px', fontWeight: 600, marginRight: 'auto' }}>{title}</span>
        <button onClick={() => handlePDFDownload(false)} title="Download PDF" style={btnStyle} disabled={downloading}>
          <FiDownload size={18} /> <span>{downloading ? 'Generating...' : 'Download PDF'}</span>
        </button>
        <button onClick={handlePrint} title="Print" style={btnStyle}>
          <FiPrinter size={18} /> <span>Print</span>
        </button>
        <button onClick={() => setShowEmailModal(true)} title="Email" style={btnStyle}>
          <FiMail size={18} /> <span>Email</span>
        </button>
        {onClose && (
          <button onClick={onClose} title="Close" style={{ ...btnStyle, background: '#dc2626' }}>
            <FiX size={18} />
          </button>
        )}
      </div>

      {/* Email Modal */}
      {showEmailModal && (
        <div className="no-print" style={{
          position: 'fixed', inset: 0, zIndex: 200,
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          background: 'rgba(0,0,0,0.5)',
        }}>
          <div style={{
            background: 'white', borderRadius: '8px', padding: '24px',
            width: '400px', boxShadow: '0 8px 32px rgba(0,0,0,0.2)',
          }}>
            <h3 style={{ margin: '0 0 16px', fontSize: '16px', color: '#1e293b' }}>Email Document</h3>
            <label style={{ fontSize: '13px', color: '#64748b', fontWeight: 500 }}>Recipient Email</label>
            <input
              type="email"
              value={emailTo}
              onChange={(e) => setEmailTo(e.target.value)}
              placeholder="recipient@example.com"
              style={{
                width: '100%', padding: '8px 12px', border: '1px solid #d1d5db',
                borderRadius: '6px', fontSize: '14px', marginTop: '4px', marginBottom: '16px',
                boxSizing: 'border-box',
              }}
              autoFocus
              onKeyDown={(e) => e.key === 'Enter' && emailTo && sendEmail()}
            />
            <div style={{ display: 'flex', gap: '8px', justifyContent: 'flex-end' }}>
              <button onClick={() => setShowEmailModal(false)} style={{
                padding: '8px 16px', border: '1px solid #d1d5db', borderRadius: '6px',
                background: 'white', cursor: 'pointer', fontSize: '13px',
              }}>Cancel</button>
              <button onClick={sendEmail} disabled={!emailTo} style={{
                padding: '8px 16px', border: 'none', borderRadius: '6px',
                background: emailTo ? '#2563eb' : '#94a3b8', color: 'white',
                cursor: emailTo ? 'pointer' : 'not-allowed', fontSize: '13px',
              }}>Open Email Client</button>
            </div>
          </div>
        </div>
      )}

      <style>{`
        @media print {
          .no-print { display: none !important; }
          body { margin: 0; }
        }
      `}</style>
    </>
  );
};

const btnStyle = {
  display: 'flex', alignItems: 'center', gap: '6px',
  padding: '6px 14px', border: 'none', borderRadius: '6px',
  background: '#334155', color: 'white', cursor: 'pointer',
  fontSize: '13px', fontWeight: 500,
};

export default DocumentToolbar;
